import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import api from '../utils/api';
import toast from 'react-hot-toast';

const LEVEL_CONFIG = {
  faible:   { label: 'Faible',   bg: '#0a1a0f', border: '#00ff7f', color: '#00ff7f', bar: '#00ff7f', dot: '#00ff7f' },
  moyen:    { label: 'Moyen',    bg: '#1a1400', border: '#d4f535', color: '#d4f535', bar: '#d4f535', dot: '#d4f535' },
  'élevé':  { label: 'Élevé',   bg: '#1a0e00', border: '#ff9500', color: '#ff9500', bar: '#ff9500', dot: '#ff9500' },
  critique: { label: 'Critique', bg: '#1a0505', border: '#ff4444', color: '#ff4444', bar: '#ff4444', dot: '#ff4444' },
};

const Icon = ({ name, size = 16, color = 'currentColor' }) => {
  const icons = {
    refresh:  <><path d="M1 4s0-3 3-3c3 0 5 2 5 2"/><path d="M1 4v4h4"/><path d="M15 12s0 3-3 3c-3 0-5-2-5-2"/><path d="M15 12v-4h-4"/></>,
    shield:   <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    alert:    <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></>,
    download: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></>,
    link:     <><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></>,
    ban:      <><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></>,
    eye:      <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>,
    user:     <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0 }}>
      {icons[name]}
    </svg>
  );
};

function RiskBar({ score, level }) {
  const conf = LEVEL_CONFIG[level] || LEVEL_CONFIG.faible;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: "#aaaaaa" }}>Score de risque</span>
        <span style={{ fontSize: 12, fontWeight: 800, color: conf.color }}>{score}/100</span>
      </div>
      <div style={{ height: 7, background: "#222", borderRadius: 999, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${score}%`, background: conf.bar, borderRadius: 999, transition: "width 0.5s ease", boxShadow: `0 0 8px ${conf.bar}99` }} />
      </div>
    </div>
  );
}

export default function RiskPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [memberRisk, setMemberRisk] = useState({});
  const [search, setSearch] = useState("");
  const [filterLevel, setFilterLevel] = useState("all");

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get("/members/risks/all");
      setMembers(data);
    } catch { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredMembers = members.filter(m => {
    const matchSearch = !search ||
      (m.name || "").toLowerCase().includes(search.toLowerCase()) ||
      (m.email || "").toLowerCase().includes(search.toLowerCase());
    const matchLevel = filterLevel === "all" || m.risk_level === filterLevel;
    return matchSearch && matchLevel;
  });

  const recalculateAll = async () => {
    setRecalculating(true);
    try {
      const { data: all } = await api.get("/members");
      for (const m of all) { await api.get(`/members/${m.id}/risk`); }
      await fetchData();
      toast.success("Scores recalculés");
    } catch { toast.error("Erreur"); }
    finally { setRecalculating(false); }
  };

  const loadMemberRisk = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (memberRisk[id]) return;
    try {
      const { data } = await api.get(`/members/${id}/risk`);
      setMemberRisk(p => ({ ...p, [id]: data }));
    } catch {}
  };

  const critiques = members.filter(m => m.risk_level === "critique").length;
  const élevés    = members.filter(m => m.risk_level === "élevé").length;

  const summaryCards = [
    { level: "faible",   count: members.filter(m => m.risk_level === "faible").length },
    { level: "moyen",    count: members.filter(m => m.risk_level === "moyen").length },
    { level: "élevé",   count: members.filter(m => m.risk_level === "élevé").length },
    { level: "critique", count: members.filter(m => m.risk_level === "critique").length },
  ];

  return (
    <div style={{ padding: 16, background: "#0a0a0a", minHeight: "100vh" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 900, color: "#ffffff", letterSpacing: -0.5 }}>
            Score de <span style={{ color: "#d4f535" }}>Risque</span>
          </h1>
          <p style={{ color: "#666", fontSize: 12, marginTop: 3 }}>Analyse automatique du comportement de chaque membre</p>
        </div>
        <button onClick={recalculateAll} disabled={recalculating} style={{ padding: "9px 16px", borderRadius: 10, border: "1.5px solid #d4f535", background: "#1a1a00", color: "#d4f535", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 7, opacity: recalculating ? 0.7 : 1 }}>
          <Icon name="refresh" size={13} color="#d4f535" />
          {recalculating ? "Calcul..." : "Recalculer"}
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, marginBottom: 16 }}>
        {summaryCards.map(({ level, count }) => {
          const conf = LEVEL_CONFIG[level];
          return (
            <div key={level} style={{ background: "#111", border: `1.5px solid ${conf.border}22`, borderLeft: `4px solid ${conf.border}`, borderRadius: 12, padding: "14px 16px" }}>
              <div style={{ width: 12, height: 12, borderRadius: "50%", background: conf.dot, boxShadow: `0 0 10px ${conf.dot}`, marginBottom: 8 }} />
              <div style={{ fontSize: 28, fontWeight: 900, color: conf.color, lineHeight: 1 }}>{count}</div>
              <div style={{ fontSize: 12, color: "#888", fontWeight: 600, marginTop: 4 }}>{conf.label}</div>
            </div>
          );
        })}
      </div>

      {(critiques > 0 || élevés > 0) && (
        <div style={{ background: "#1a0505", border: "1.5px solid #ff444488", borderRadius: 14, padding: 14, marginBottom: 16, display: "flex", gap: 10, alignItems: "flex-start" }}>
          <Icon name="alert" size={18} color="#ff4444" />
          <div>
            <div style={{ fontWeight: 700, color: "#ff4444", fontSize: 13 }}>{critiques} critique(s) · {élevés} élevé(s) détecté(s)</div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 3 }}>Vérifiez immédiatement les membres en rouge ci-dessous.</div>
          </div>
        </div>
      )}

      <div style={{ background: "#111", borderRadius: 14, border: "1px solid #222", padding: 14, marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: "#d4f535", marginBottom: 12, display: "flex", alignItems: "center", gap: 7 }}>
          <Icon name="shield" size={14} color="#d4f535" /> Règles de calcul
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          {[
            { label: "Téléchargement", pts: "+60" }, { label: "Lien partagé", pts: "+50" },
            { label: "Pays étranger", pts: "+40" },  { label: "Email inconnu", pts: "+35" },
            { label: "Plusieurs appareils", pts: "+30" }, { label: "Plusieurs IPs", pts: "+25" },
            { label: "Tentative impression", pts: "+25" }, { label: "Tentative copier", pts: "+20" },
            { label: "DevTools ouverts", pts: "+15" }, { label: "Nombreuses ouvertures", pts: "+15" },
          ].map((r, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#0d0d0d", borderRadius: 7, padding: "6px 10px", border: "1px solid #1a1a1a" }}>
              <span style={{ fontSize: 11, color: "#cccccc" }}>{r.label}</span>
              <span style={{ fontSize: 12, fontWeight: 900, color: "#d4f535", fontFamily: "monospace" }}>{r.pts}</span>
            </div>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 48, color: "#555" }}>Chargement...</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
            <input type="text" placeholder="Rechercher par nom ou email..." value={search} onChange={e => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: 200, padding: "10px 14px", borderRadius: 10, border: "1.5px solid #333", fontSize: 13, outline: "none", background: "#111", color: "#ffffff" }} />
            <select value={filterLevel} onChange={e => setFilterLevel(e.target.value)}
              style={{ padding: "10px 14px", borderRadius: 10, border: "1.5px solid #333", fontSize: 13, background: "#111", color: "#ffffff", cursor: "pointer" }}>
              <option value="all">Tous les niveaux</option>
              <option value="critique">Critique</option>
              <option value="élevé">Élevé</option>
              <option value="moyen">Moyen</option>
              <option value="faible">Faible</option>
            </select>
          </div>
          <div style={{ marginBottom: 8, fontSize: 12, color: "#555" }}>{filteredMembers.length} membre(s) affiché(s) sur {members.length}</div>
          {filteredMembers.sort((a,b) => (b.risk_score||0)-(a.risk_score||0)).map(m => {
            const level = m.risk_level || "faible";
            const conf = LEVEL_CONFIG[level] || LEVEL_CONFIG.faible;
            const isExpanded = expanded === m.id;
            const risk = memberRisk[m.id];
            return (
              <div key={m.id} style={{ background: "#111", border: "1px solid #1e1e1e", borderLeft: `4px solid ${conf.border}`, borderRadius: 14, overflow: "hidden" }}>
                <div onClick={() => loadMemberRisk(m.id)} style={{ padding: 16, cursor: "pointer" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: "50%", background: `${conf.dot}22`, border: `2px solid ${conf.dot}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <div style={{ width: 14, height: 14, borderRadius: "50%", background: conf.dot, boxShadow: `0 0 10px ${conf.dot}` }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 14, fontWeight: 800, color: "#ffffff" }}>{m.name}</span>
                        <span style={{ background: `${conf.border}22`, color: conf.color, border: `1px solid ${conf.border}`, borderRadius: 999, padding: "2px 9px", fontSize: 11, fontWeight: 800 }}>{conf.label}</span>
                      </div>
                      <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{m.email}</div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontSize: 26, fontWeight: 900, color: conf.color, lineHeight: 1 }}>{m.risk_score || 0}</div>
                      <div style={{ fontSize: 10, color: "#555" }}>/ 100</div>
                    </div>
                  </div>
                  <RiskBar score={m.risk_score || 0} level={level} />
                  <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                    {m.has_downloaded && <span style={{ background: "#ff444422", color: "#ff4444", border: "1px solid #ff444466", borderRadius: 6, padding: "3px 8px", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}><Icon name="download" size={10} color="#ff4444" /> Téléchargé</span>}
                    {m.has_shared_link && <span style={{ background: "#d4f53522", color: "#d4f535", border: "1px solid #d4f53566", borderRadius: 6, padding: "3px 8px", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}><Icon name="link" size={10} color="#d4f535" /> Lien partagé</span>}
                    {m.unauthorized_attempts > 0 && <span style={{ background: "#ff444422", color: "#ff4444", border: "1px solid #ff444466", borderRadius: 6, padding: "3px 8px", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}><Icon name="ban" size={10} color="#ff4444" /> {m.unauthorized_attempts} refus</span>}
                    <span style={{ background: "#4db8ff22", color: "#4db8ff", border: "1px solid #4db8ff66", borderRadius: 6, padding: "3px 8px", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}><Icon name="eye" size={10} color="#4db8ff" /> {m.total_opens} ouvertures</span>
                  </div>
                  <div style={{ fontSize: 11, color: "#555", marginTop: 8 }}>{isExpanded ? "▲ Réduire" : "▼ Voir le détail des facteurs de risque"}</div>
                </div>
                {isExpanded && (
                  <div style={{ padding: "0 16px 16px", borderTop: "1px solid #1e1e1e" }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: "#d4f535", margin: "12px 0 8px" }}>Facteurs de risque détectés</div>
                    {!risk ? (
                      <div style={{ color: "#555", fontSize: 12 }}>Chargement...</div>
                    ) : risk.triggers.length === 0 ? (
                      <div style={{ color: "#00ff7f", fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}><Icon name="shield" size={12} color="#00ff7f" /> Aucun facteur de risque détecté</div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {risk.triggers.map((t, i) => (
                          <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#0d0d0d", borderRadius: 8, padding: "8px 12px", border: "1px solid #1e1e1e" }}>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: "#ffffff" }}>{t.label}</div>
                              {t.detail && <div style={{ fontSize: 11, color: "#666" }}>{t.detail}</div>}
                            </div>
                            <span style={{ background: "#ff444422", color: "#ff4444", border: "1px solid #ff4444", borderRadius: 999, padding: "2px 9px", fontSize: 11, fontWeight: 900, fontFamily: "monospace" }}>+{t.points}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
